import React, { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, AlertTriangle } from "lucide-react";

// --- Date helpers (local, no external deps) ---
const pad = (n) => String(n).padStart(2, "0");
const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const addDays = (d, n) => {
  const c = new Date(d);
  c.setDate(c.getDate() + n);
  return c;
};
// Monday-first week: getDay() returns 0(Sun)..6(Sat). We want Mon=0..Sun=6.
const mondayOf = (d) => {
  const c = new Date(d);
  const dow = (c.getDay() + 6) % 7; // Mon=0
  c.setDate(c.getDate() - dow);
  c.setHours(0, 0, 0, 0);
  return c;
};

// Parse loose SA-style times like "08H00", "8:30", "14h15", "9" -> hour integer (0-23) or null
const parseHour = (raw) => {
  if (!raw) return null;
  const s = String(raw).trim().toLowerCase();
  const m = s.match(/(\d{1,2})/);
  if (!m) return null;
  const h = parseInt(m[1], 10);
  if (Number.isNaN(h) || h < 0 || h > 23) return null;
  return h;
};

const HOURS = [7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17];
const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export const BookingsWeekView = ({ bookings, onEdit, onCreateAt }) => {
  const [anchor, setAnchor] = useState(() => mondayOf(new Date()));

  const weekDays = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(anchor, i)),
    [anchor]
  );
  const weekKeys = weekDays.map(toISO);
  const todayISO = toISO(new Date());

  // Bucket bookings into { dateISO: { hour|"none": [b, ...] } }, only for current week
  const buckets = useMemo(() => {
    const map = {};
    weekKeys.forEach((k) => (map[k] = { none: [] }));
    HOURS.forEach((h) => weekKeys.forEach((k) => (map[k][h] = [])));

    for (const b of bookings) {
      if (!weekKeys.includes(b.fitment_date)) continue;
      const h = parseHour(b.fitment_time);
      if (h === null || !HOURS.includes(h)) {
        map[b.fitment_date].none.push(b);
      } else {
        map[b.fitment_date][h].push(b);
      }
    }
    return map;
  }, [bookings, weekKeys.join(",")]);

  // Fitter-conflict detection across the whole week (same fitter, same day+hour, >1 booking)
  const fitterConflicts = useMemo(() => {
    const set = new Set();
    for (const key of weekKeys) {
      for (const h of HOURS) {
        const cell = buckets[key][h] || [];
        const byFitter = {};
        for (const b of cell) {
          const f = (b.fitter_team_leader || "").trim().toLowerCase();
          if (!f) continue;
          (byFitter[f] = byFitter[f] || []).push(b.id);
        }
        Object.values(byFitter).forEach((ids) => {
          if (ids.length > 1) ids.forEach((id) => set.add(id));
        });
      }
    }
    return set;
  }, [buckets, weekKeys.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps

  const weekLabel = `${weekDays[0].toLocaleDateString("en-ZA", { day: "numeric", month: "short" })} – ${weekDays[6].toLocaleDateString("en-ZA", { day: "numeric", month: "short", year: "numeric" })}`;

  return (
    <div data-testid="bookings-week-view" className="mt-6">
      {/* Nav */}
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="inline-flex items-center gap-1 bg-white border border-[#cfd7db] rounded-md shadow-sm">
          <button
            onClick={() => setAnchor(addDays(anchor, -7))}
            className="px-3 h-9 text-[#3f4b52] hover:text-[#f28a16] inline-flex items-center gap-1"
            data-testid="week-prev-btn"
            aria-label="Previous week"
          >
            <ChevronLeft size={16} /> Prev
          </button>
          <button
            onClick={() => setAnchor(mondayOf(new Date()))}
            className="px-3 h-9 text-xs font-display uppercase tracking-widest text-[#111619] border-l border-r border-[#cfd7db] hover:text-[#f28a16]"
            data-testid="week-today-btn"
          >
            This week
          </button>
          <button
            onClick={() => setAnchor(addDays(anchor, 7))}
            className="px-3 h-9 text-[#3f4b52] hover:text-[#f28a16] inline-flex items-center gap-1"
            data-testid="week-next-btn"
            aria-label="Next week"
          >
            Next <ChevronRight size={16} />
          </button>
        </div>
        <div
          className="font-display uppercase tracking-widest text-sm text-[#111619]"
          data-testid="week-label"
        >
          {weekLabel}
        </div>
        <div className="text-xs text-[#3f4b52] inline-flex items-center gap-2">
          <span className="inline-block w-3 h-3 rounded-sm bg-[#fef2f2] border border-[#dc2626]" />
          Conflict / double-booking
        </div>
      </div>

      {/* Grid */}
      <div className="bg-white border border-[#cfd7db] rounded-md shadow-sm overflow-x-auto">
        <div className="min-w-[900px]">
          {/* Header row */}
          <div className="grid grid-cols-[80px_repeat(7,minmax(0,1fr))] border-b border-[#eef2f4] bg-[#edf2f4]">
            <div className="px-2 py-2 text-[10px] font-display uppercase tracking-widest text-[#3f4b52]">
              Time
            </div>
            {weekDays.map((d, i) => {
              const iso = toISO(d);
              const isToday = iso === todayISO;
              return (
                <div
                  key={iso}
                  className={`px-2 py-2 text-center border-l border-[#eef2f4] ${
                    isToday ? "bg-[#fff7ed]" : ""
                  }`}
                  data-testid={`week-day-header-${iso}`}
                >
                  <div className="text-[10px] font-display uppercase tracking-widest text-[#3f4b52]">
                    {DAY_LABELS[i]}
                  </div>
                  <div
                    className={`text-sm font-display font-bold ${
                      isToday ? "text-[#f28a16]" : "text-[#111619]"
                    }`}
                  >
                    {d.getDate()} {d.toLocaleDateString("en-ZA", { month: "short" })}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Hour rows */}
          {HOURS.map((h) => (
            <div
              key={h}
              className="grid grid-cols-[80px_repeat(7,minmax(0,1fr))] border-b border-[#eef2f4]"
            >
              <div className="px-2 py-2 text-xs font-display font-bold text-[#3f4b52] flex items-start">
                {pad(h)}:00
              </div>
              {weekKeys.map((iso) => {
                const items = buckets[iso][h] || [];
                const isConflict = items.length > 1;
                const isToday = iso === todayISO;
                return (
                  <div
                    key={`${iso}-${h}`}
                    className={`min-h-[60px] p-1 border-l border-[#eef2f4] ${
                      isToday ? "bg-[#fffbf5]" : ""
                    } ${isConflict ? "bg-[#fef2f2]" : ""} cursor-pointer hover:bg-[#f7fafc] transition`}
                    onClick={(e) => {
                      // Only trigger create if clicking the empty background, not a booking chip
                      if (e.target === e.currentTarget) {
                        onCreateAt && onCreateAt(iso, `${pad(h)}H00`);
                      }
                    }}
                    data-testid={`week-cell-${iso}-${h}`}
                  >
                    {isConflict && (
                      <div className="text-[9px] font-display uppercase tracking-widest text-[#dc2626] inline-flex items-center gap-0.5 mb-1">
                        <AlertTriangle size={9} /> Conflict
                      </div>
                    )}
                    {items.map((b) => {
                      const fitterConflict = fitterConflicts.has(b.id);
                      return (
                        <button
                          key={b.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            onEdit && onEdit(b);
                          }}
                          className={`block w-full text-left mb-1 px-2 py-1 rounded text-[11px] leading-tight border transition ${
                            isConflict || fitterConflict
                              ? "bg-white border-[#dc2626] text-[#7c2d12] hover:bg-[#fff7ed]"
                              : "bg-[#f28a16] border-[#f28a16] text-white hover:brightness-110"
                          }`}
                          data-testid={`week-booking-${b.id}`}
                          title={`${b.client_name} · ${b.registration}${b.fitter_team_leader ? " · " + b.fitter_team_leader : ""}`}
                        >
                          <div className="font-display font-bold">
                            {b.fitment_time || `${pad(h)}H00`} · {b.registration || "—"}
                          </div>
                          <div className="truncate opacity-90">{b.client_name}</div>
                          {b.fitter_team_leader && (
                            <div className="truncate opacity-80 text-[10px]">
                              {b.fitter_team_leader}
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          ))}

          {/* Unscheduled row (no time) — only render if there are any this week */}
          {weekKeys.some((k) => (buckets[k].none || []).length > 0) && (
            <div className="grid grid-cols-[80px_repeat(7,minmax(0,1fr))]">
              <div className="px-2 py-2 text-[10px] font-display uppercase tracking-widest text-[#3f4b52]">
                No time
              </div>
              {weekKeys.map((iso) => {
                const items = buckets[iso].none || [];
                const isConflict = items.length > 1;
                return (
                  <div
                    key={`${iso}-none`}
                    className={`min-h-[50px] p-1 border-l border-t border-[#eef2f4] ${
                      isConflict ? "bg-[#fef2f2]" : ""
                    }`}
                    data-testid={`week-cell-${iso}-none`}
                  >
                    {items.map((b) => (
                      <button
                        key={b.id}
                        onClick={() => onEdit && onEdit(b)}
                        className={`block w-full text-left mb-1 px-2 py-1 rounded text-[11px] leading-tight border transition ${
                          isConflict
                            ? "bg-white border-[#dc2626] text-[#7c2d12] hover:bg-[#fff7ed]"
                            : "bg-[#3f4b52] border-[#3f4b52] text-white hover:brightness-110"
                        }`}
                        data-testid={`week-booking-${b.id}`}
                        title={`${b.client_name} · ${b.registration}`}
                      >
                        <div className="font-display font-bold">{b.registration || "—"}</div>
                        <div className="truncate opacity-90">{b.client_name}</div>
                      </button>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <p className="mt-2 text-xs text-[#3f4b52]">
        Tip: click an empty cell to create a booking at that day &amp; time. Click a chip to edit.
      </p>
    </div>
  );
};

export default BookingsWeekView;
